// Funnel B operator surface.
//
// Phase 11 of the Consumer Speed-to-Lead Engine build. Reads
// /api/funnel-b/snapshot and renders the dashboard the operator
// uses to monitor:
//   · Per-lead status (stage, consent, scrub, automation_eligible)
//   · Per-tenant number-pool health
//   · Recent AI activity
//   · 24h anomaly throttle status (normal / throttle / suspend)
//
// Read-only for V1. Write actions (toggle AI-voice, manage agents,
// rotate ingest keys, etc.) come in follow-up sub-views once the
// pipeline has live traffic to display.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import {
  Loader2, RefreshCcw, ShieldCheck, AlertTriangle, Phone,
  MessageSquare, Activity, Users, ChevronRight, Settings, BarChart3,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import FunnelBManage from './FunnelBManage';

async function apiFetch(path) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const THROTTLE_TONE = {
  normal:   { color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200', label: 'Normal' },
  throttle: { color: 'text-amber-800',   bg: 'bg-amber-50',    border: 'border-amber-200',   label: 'Throttled' },
  suspend:  { color: 'text-rose-800',    bg: 'bg-rose-50',     border: 'border-rose-200',    label: 'Suspended' },
};

function StatTile({ label, value, sub, tone = 'slate' }) {
  return (
    <div className="bg-slate-950 px-3 py-2 text-center">
      <div className={`text-2xl font-black tabular-nums text-${tone}-400`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">{label}</div>
      {sub && <div className="text-[9px] text-slate-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function HealthChip({ status, count }) {
  const tones = {
    warming:    'bg-amber-100 text-amber-800',
    active:     'bg-emerald-100 text-emerald-800',
    watch:      'bg-amber-200 text-amber-900',
    quarantine: 'bg-rose-100 text-rose-800',
    retired:    'bg-slate-200 text-slate-700',
    released:   'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${tones[status] || 'bg-slate-100 text-slate-700'}`}>
      {status}: {count}
    </span>
  );
}

export default function FunnelBView({ currentUser, showToast = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [mode, setMode]       = React.useState('dashboard');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/funnel-b/snapshot');
      setData(r);
    } catch (err) {
      showToast(`Funnel B snapshot failed: ${err.message}`, 'error');
      setData({ ok: false, summary: {} });
    } finally {
      setLoading(false);
    }
  }, [showToast]);
  React.useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const throttle = s.anomaly_window_24h || {};
  const throttleTone = THROTTLE_TONE[throttle.action] || THROTTLE_TONE.normal;

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-950 to-indigo-950/40 border border-indigo-900/30 rounded-xl p-5 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-indigo-400/80 flex items-center gap-2">
              <Activity size={11}/> Funnel B · consumer speed-to-lead
            </div>
            <h1 className="text-xl font-black mt-1.5">The agent's pipeline, automated.</h1>
            <p className="text-xs text-slate-400 mt-1">
              Purchased consumer leads → consent-verified → scrub-cleared → PILOT qualifies + books → warm-transfer to a licensed agent.
              Everything runs under YOUR A2P + consent + recording config — never SFA's.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-900/60 rounded-md p-0.5 inline-flex">
              <button onClick={() => setMode('dashboard')}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded inline-flex items-center gap-1 ${mode === 'dashboard' ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:text-white'}`}>
                <BarChart3 size={10}/> Dashboard
              </button>
              <button onClick={() => setMode('manage')}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded inline-flex items-center gap-1 ${mode === 'manage' ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:text-white'}`}>
                <Settings size={10}/> Manage
              </button>
            </div>
            {mode === 'dashboard' && (
              <button onClick={load} disabled={loading} className="text-xs text-indigo-200/70 hover:text-indigo-200 inline-flex items-center gap-1 disabled:opacity-30">
                <RefreshCcw size={11}/> Refresh
              </button>
            )}
          </div>
        </div>

        {mode === 'dashboard' && (
          <>
            {/* Anomaly throttle banner */}
            <div className={`mt-4 border rounded-lg p-3 ${throttleTone.bg} ${throttleTone.border}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {throttle.action === 'suspend' ? <AlertTriangle size={14} className="text-rose-700"/>
                    : throttle.action === 'throttle' ? <AlertTriangle size={14} className="text-amber-700"/>
                    : <ShieldCheck size={14} className="text-emerald-700"/>}
                  <span className={`font-bold text-sm ${throttleTone.color}`}>
                    24h anomaly status: {throttleTone.label}
                  </span>
                  <span className="text-xs text-slate-600">· {throttle.reason || 'no_signal'}</span>
                </div>
                <div className="text-xs text-slate-700 tabular-nums">
                  {throttle.touches || 0} touches · {throttle.complaints || 0} complaints ({((throttle.complaint_rate || 0) * 100).toFixed(2)}%) ·
                  {throttle.spamFlags || 0} spam ({((throttle.spam_rate || 0) * 100).toFixed(2)}%)
                </div>
              </div>
            </div>

            {/* Lead summary tiles */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-px bg-indigo-900/20 rounded-lg overflow-hidden">
              <StatTile label="Leads"     value={(s.leads?.total || 0).toLocaleString()}                        tone="indigo"/>
              <StatTile label="Eligible"  value={(s.leads?.automation_eligible || 0).toLocaleString()}         tone="emerald" sub="ready to dispatch"/>
              <StatTile label="Opted out" value={(s.leads?.opted_out || 0).toLocaleString()}                   tone="rose"    sub="cross-channel suppressed"/>
              <StatTile label="Numbers"   value={(s.numbers?.total || 0).toLocaleString()}                     tone="amber"   sub={`${s.numbers?.auto_provisioned || 0} auto-provisioned`}/>
              <StatTile label="Optouts"   value={(s.optouts?.total || 0).toLocaleString()}                     tone="rose"/>
            </div>
          </>
        )}
      </div>

      {mode === 'manage' ? (
        <FunnelBManage showToast={showToast}/>
      ) : loading && !data ? (
        <div className="text-center py-12 text-slate-500"><Loader2 size={20} className="animate-spin inline mr-2"/>Loading Funnel B snapshot…</div>
      ) : (
        <>
          {/* Lead stage breakdown */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Lead stage breakdown</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(s.leads?.by_stage || {}).map(([stage, count]) => (
                <span key={stage} className="text-xs font-bold px-2 py-1 rounded bg-slate-100 text-slate-800">
                  {stage}: <span className="tabular-nums">{count}</span>
                </span>
              ))}
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Consent coverage</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(s.leads?.by_consent_status || {}).map(([status, count]) => {
                const tone =
                  status === 'verified'  ? 'bg-emerald-100 text-emerald-800' :
                  status === 'revoked'   ? 'bg-rose-100 text-rose-800' :
                  status === 'expired'   ? 'bg-amber-100 text-amber-800' :
                                            'bg-slate-100 text-slate-700';
                return (
                  <span key={status} className={`text-xs font-bold px-2 py-1 rounded ${tone}`}>
                    {status}: <span className="tabular-nums">{count}</span>
                  </span>
                );
              })}
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Scrub coverage</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(s.leads?.by_scrub_status || {}).map(([status, count]) => {
                const tone =
                  status === 'passed'  ? 'bg-emerald-100 text-emerald-800' :
                  status === 'failed'  ? 'bg-rose-100 text-rose-800' :
                                          'bg-slate-100 text-slate-700';
                return (
                  <span key={status} className={`text-xs font-bold px-2 py-1 rounded ${tone}`}>
                    {status}: <span className="tabular-nums">{count}</span>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Number-pool health */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
              <Phone size={11}/> Per-tenant number-pool health
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {Object.entries(s.numbers?.by_status || {}).map(([status, count]) => (
                <HealthChip key={status} status={status} count={count}/>
              ))}
            </div>
            {(data?.numbers || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic">No numbers in pool yet. Onboarding provisions the first one.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left p-1.5">Number</th>
                    <th className="text-left p-1.5">NPA</th>
                    <th className="text-left p-1.5">Status</th>
                    <th className="text-right p-1.5">Sent today</th>
                    <th className="text-right p-1.5">Cap</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.numbers || []).slice(0, 20).map(n => (
                    <tr key={n.e164} className="border-t border-slate-100">
                      <td className="p-1.5 font-mono">{n.e164}</td>
                      <td className="p-1.5">{n.npa}</td>
                      <td className="p-1.5"><HealthChip status={n.status} count="" /></td>
                      <td className="p-1.5 text-right tabular-nums">{n.sent_today || 0}</td>
                      <td className="p-1.5 text-right tabular-nums">{n.daily_cap || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent AI activity */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
              <MessageSquare size={11}/> Recent activity (last 25)
            </div>
            {(data?.recent_activity || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic">No activity yet. Hit the ingest webhook to get rolling.</div>
            ) : (
              <ul className="space-y-1">
                {(data?.recent_activity || []).map(a => {
                  const isAi = Boolean(a.ai_model);
                  return (
                    <li key={a.id} className="text-xs text-slate-700 flex items-center gap-2">
                      <span className="text-slate-400 tabular-nums">{new Date(a.created_at).toLocaleTimeString()}</span>
                      <span
                        title={isAi ? `Sent by PILOT (${a.ai_model})` : 'Sent by a human agent'}
                        className={`font-bold uppercase tracking-wider text-[9px] px-1.5 py-0.5 rounded ${
                          isAi ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {isAi ? 'AI' : 'Human'}
                      </span>
                      <span className="font-bold uppercase tracking-wider text-[9px] bg-slate-100 px-1.5 py-0.5 rounded">{a.channel}</span>
                      <span className="text-[9px] text-slate-500">{a.direction}</span>
                      <span className="font-mono text-slate-700">{a.outcome || '—'}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
